import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FIDELIDADE_PLANO,
  FREQUENCIA_PLANO,
  MODALIDADE,
  type FidelidadePlano,
  type FrequenciaPlano,
  type Modalidade,
} from '@arena/shared/dominio';
import {
  ROTULO_FIDELIDADE_PLANO,
  ROTULO_FREQUENCIA_PLANO,
  ehModalidadePersonalizada,
  ROTULO_MODALIDADE,
  STATUS_PLANO,
  textoDoPercentual,
  zAlteracaoDePlano,
  zNovoPlano,
  type PlanoNaLista,
  type StatusPlano,
  type TipoDeDesconto,
} from '@arena/shared/schemas';
import { dataCurta, dinheiro, paraCentavos } from '../agenda/formatos.js';
import { errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { planosApi } from '../api/planos.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Escolhas, Formulario, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { centesimosParaCampo, percentualParaCentesimos } from './formatos.js';

/**
 * `#m-plano` — o produto recorrente que gera as faturas do mês.
 *
 * Valor e dia de vencimento são os de TABELA: o que uma assinatura nova
 * recebe. Quem já assinou tem o próprio contrato — a nota do modal diz
 * isso, porque é a pergunta que a pessoa vai fazer ao mudar o preço. Para
 * mudar o valor de quem já assinou existe o reajuste em lote, com vigência.
 *
 * Os três lembretes ligam e desligam o `lembreteAtivo` dos eventos da régua
 * do plano. Quem transforma o evento em mensagem é o documento 15.
 *
 * Sem permissão de editar, o modal abre só para ler — o servidor recusaria
 * de qualquer forma.
 */

const DIAS_DO_MES = Array.from({ length: 31 }, (_, i) => i + 1);

type Lembrete = 'antes' | 'noDia' | 'depois';

const LEMBRETES: readonly { valor: Lembrete; rotulo: string }[] = [
  { valor: 'antes', rotulo: '3 dias antes' },
  { valor: 'noDia', rotulo: 'No dia' },
  { valor: 'depois', rotulo: 'Após o vencimento' },
];

export function ModalDePlano({
  plano,
  onFechar,
}: {
  /** Ausente: plano novo. */
  plano?: PlanoNaLista;
  onFechar: () => void;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const editando = Boolean(plano);
  const podeSalvar = editando ? pode('planos', 'editar') : pode('planos', 'criar');

  const [nome, setNome] = useState(plano?.nome ?? '');
  const [modalidade, setModalidade] = useState<Modalidade>(plano?.modalidade ?? 'Futvolei');
  const [modalidadeOutra, setModalidadeOutra] = useState(plano?.modalidadeOutra ?? '');
  const personalizada = ehModalidadePersonalizada(modalidade);
  const [frequencia, setFrequencia] = useState<FrequenciaPlano>(plano?.frequencia ?? 'DuasVezes');
  const [valor, setValor] = useState(plano ? String(plano.valor / 100).replace('.', ',') : '');
  const [fidelidade, setFidelidade] = useState<FidelidadePlano>(plano?.fidelidade ?? 'Mensal');
  const [dia, setDia] = useState(plano?.diaVencimento ?? 5);
  const [pessoas, setPessoas] = useState(plano?.pessoas ?? 1);
  const [tipoDeDesconto, setTipoDeDesconto] = useState<TipoDeDesconto | ''>(
    plano?.desconto?.tipo ?? '',
  );
  const [desconto, setDesconto] = useState(
    plano?.desconto
      ? plano.desconto.tipo === 'Percentual'
        ? centesimosParaCampo(plano.desconto.valor)
        : String(plano.desconto.valor / 100).replace('.', ',')
      : '',
  );
  const [status, setStatus] = useState<StatusPlano>(plano?.status ?? 'Ativo');
  const [lembretes, setLembretes] = useState<Record<Lembrete, boolean>>(
    plano?.lembretes ?? { antes: true, noDia: true, depois: true },
  );
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: (corpo: unknown) =>
      plano
        ? planosApi.alterar(plano.id, zAlteracaoDePlano.parse(corpo))
        : planosApi.criar(zNovoPlano.parse(corpo)),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['planos'] }),
  });

  function montar(): Record<string, unknown> | null {
    const centavos = paraCentavos(valor);
    if (centavos === null) {
      setErros({
        valor: valor.trim() ? 'Valor inválido — use 160 ou 160,50.' : 'Informe o valor.',
      });
      return null;
    }
    let descontoFinal: { tipo: TipoDeDesconto; valor: number } | null = null;
    if (tipoDeDesconto) {
      const numero =
        tipoDeDesconto === 'Percentual'
          ? percentualParaCentesimos(desconto)
          : paraCentavos(desconto);
      if (numero === null || numero <= 0) {
        setErros({ desconto: 'Desconto inválido — "10%" ou "20,00".' });
        return null;
      }
      descontoFinal = { tipo: tipoDeDesconto, valor: numero };
    }
    const corpo = {
      nome: nome.trim(),
      modalidade,
      // `null` e não ausente: ao sair de "Outra" para uma modalidade da
      // lista, o nome livre tem que ser APAGADO — o diff abaixo compara
      // com o gravado e manda os dois campos juntos.
      modalidadeOutra: personalizada ? modalidadeOutra.trim() : null,
      frequencia,
      valor: centavos,
      fidelidade,
      diaVencimento: dia,
      pessoas,
      desconto: descontoFinal,
      status,
      lembretes,
    };
    if (!plano) return corpo;

    // Na edição, só o que mudou: o servidor recusa pedido que repete o gravado.
    const mudou: Record<string, unknown> = {};
    for (const [chave, novo] of Object.entries(corpo)) {
      const atual = (plano as unknown as Record<string, unknown>)[chave];
      if (JSON.stringify(atual ?? null) !== JSON.stringify(novo ?? null)) mudou[chave] = novo;
    }
    return mudou;
  }

  async function confirmar() {
    setErros({});
    setErroGeral(null);
    const corpo = montar();
    if (!corpo) return;
    if (plano && Object.keys(corpo).length === 0) {
      onFechar();
      return;
    }
    const pedido = (plano ? zAlteracaoDePlano : zNovoPlano).safeParse(corpo);
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }
    try {
      await salvar.mutateAsync(pedido.data);
      avisar(
        plano
          ? 'Plano salvo. As próximas assinaturas e faturas já usam esta configuração.'
          : 'Plano criado.',
      );
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  const somenteLeitura = !podeSalvar;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Cadastro de plano"
      descricao="Produto recorrente que gera as faturas do mês."
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            {somenteLeitura ? 'Fechar' : 'Cancelar'}
          </Botao>
          {!somenteLeitura && (
            <Botao onClick={() => void confirmar()} disabled={salvar.isPending}>
              {salvar.isPending ? 'Salvando…' : 'Salvar plano'}
            </Botao>
          )}
        </>
      }
    >
      <CorpoDeModal>
        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Nome" erro={erros['nome']}>
            <input
              value={nome}
              disabled={somenteLeitura}
              placeholder="Futevôlei 2x por semana"
              onChange={(e) => setNome(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Modalidade" erro={erros['modalidade']}>
            <select
              value={modalidade}
              disabled={somenteLeitura}
              onChange={(e) => setModalidade(e.target.value as Modalidade)}
            >
              {MODALIDADE.map((m) => (
                <option key={m} value={m}>
                  {m === 'Outra' ? 'Outra — digitar o nome' : ROTULO_MODALIDADE[m]}
                </option>
              ))}
            </select>
          </Campo>
          {personalizada && (
            <Campo rotulo="Nome da modalidade" erro={erros['modalidadeOutra']}>
              <input
                maxLength={40}
                disabled={somenteLeitura}
                placeholder="Ritmos, treino de saque…"
                value={modalidadeOutra}
                onChange={(e) => setModalidadeOutra(e.target.value)}
              />
            </Campo>
          )}
          <Campo rotulo="Frequência" erro={erros['frequencia']}>
            <select
              value={frequencia}
              disabled={somenteLeitura}
              onChange={(e) => setFrequencia(e.target.value as FrequenciaPlano)}
            >
              {FREQUENCIA_PLANO.map((f) => (
                <option key={f} value={f}>
                  {ROTULO_FREQUENCIA_PLANO[f]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Valor mensal" erro={erros['valor']}>
            <input
              inputMode="decimal"
              placeholder="R$ 160,00"
              value={valor}
              disabled={somenteLeitura}
              onChange={(e) => setValor(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Fidelidade" erro={erros['fidelidade']}>
            <select
              value={fidelidade}
              disabled={somenteLeitura}
              onChange={(e) => setFidelidade(e.target.value as FidelidadePlano)}
            >
              {FIDELIDADE_PLANO.map((f) => (
                <option key={f} value={f}>
                  {ROTULO_FIDELIDADE_PLANO[f]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            rotulo="Dia de vencimento"
            erro={erros['diaVencimento']}
            ajuda={dia > 28 ? 'Nos meses mais curtos, vence no último dia.' : undefined}
          >
            <select
              value={dia}
              disabled={somenteLeitura}
              onChange={(e) => setDia(Number(e.target.value))}
            >
              {DIAS_DO_MES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Desconto" erro={erros['desconto']}>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={tipoDeDesconto}
                disabled={somenteLeitura}
                onChange={(e) => setTipoDeDesconto(e.target.value as TipoDeDesconto | '')}
                style={{ maxWidth: 130 }}
              >
                <option value="">Sem desconto</option>
                <option value="Percentual">%</option>
                <option value="Valor">R$</option>
              </select>
              <input
                value={desconto}
                disabled={somenteLeitura || !tipoDeDesconto}
                placeholder="indicação, plano família"
                onChange={(e) => setDesconto(e.target.value)}
              />
            </div>
          </Campo>
          <Campo rotulo="Pessoas por assinatura" erro={erros['pessoas']}>
            <select
              value={pessoas}
              disabled={somenteLeitura}
              onChange={(e) => setPessoas(Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n === 1 ? '1 pessoa' : `Até ${n} pessoas`}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Status" erro={erros['status']}>
            <select
              value={status}
              disabled={somenteLeitura}
              onChange={(e) => setStatus(e.target.value as StatusPlano)}
            >
              {STATUS_PLANO.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Campo>
        </Formulario>

        {/* A régua PUBLICA os eventos de cobrança; quem avisa a pessoa é a
            recepção, olhando a fila. Não há envio automático no produto. */}
        <Campo
          rotulo="Lembretes de cobrança"
          ajuda="Marcam quais avisos a régua publica — três dias antes, no dia e depois do vencimento."
          erro={erros['lembretes']}
        >
          <Escolhas
            aria-label="Lembretes do plano"
            opcoes={LEMBRETES}
            valor={LEMBRETES.filter((l) => lembretes[l.valor]).map((l) => l.valor)}
            onChange={(l) => !somenteLeitura && setLembretes({ ...lembretes, [l]: !lembretes[l] })}
          />
        </Campo>

        {plano?.reajusteProgramado && (
          <Nota>
            Reajuste de <b>{textoDoPercentual(plano.reajusteProgramado.percentual)}</b> programado
            para {dataCurta(plano.reajusteProgramado.vigencia)}: o valor passa a{' '}
            <b>{dinheiro(plano.reajusteProgramado.valor)}</b>.
          </Nota>
        )}
        <Nota>
          Valor e dia de vencimento valem para as assinaturas <b>novas</b>. Quem já assinou mantém o
          contrato — mudar o valor dessas pessoas é o reajuste em lote, com vigência.
        </Nota>

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
